import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Box, TableBody, TableContainer, TableHead, TableRow, TableSortLabel, Typography, Button } from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import { spinnerActions } from '../../../actions'
import { documentKey, documentTypeLabels, formatMoney } from '../../../helpers/logisticaHelper'
import {
    StyledTable,
    StyledHeaderCell,
    StyledBodyCell,
    EmptyState,
    ActionsContainer,
    LeftActions,
} from '../../../ui/StyledComponents'
import PlanillaRepartoRow, { notaConNotaSx } from './PlanillaRepartoRow'
import ColumnFilter from './ColumnFilter'

const actionsSx = { borderTop: 'none', pt: 0 }
const leyendaSx = { display: 'flex', alignItems: 'center', gap: 1 }
const leyendaIconSx = { fontSize: 16 }
const tableContainerSx = { maxHeight: { xs: 360, sm: 480, md: '58vh' }, overflow: 'auto' }

// Mismo iluminado que el botón de la nota, sin hover: la leyenda no se puede tocar
const notaLeyendaSx = {
    ...notaConNotaSx,
    '&:hover': undefined,
    display: 'inline-flex',
    borderRadius: '50%',
    p: '4px',
}

const textValue = (value) => String(value ?? '')

// Columnas de la grilla; las que tienen `field` se pueden ordenar con el encabezado y las `filterable` tienen
// el buscador de valores (solo columnas que no se editan: sus grupos no cambian mientras se escribe)
const columns = [
    { label: 'Tipo', align: 'center', field: 'documentType', value: (d) => documentTypeLabels[d.documentType] ?? textValue(d.documentType) },
    { label: 'Cliente', align: 'center', field: 'customerName', value: (d) => textValue(d.customerName), filterable: true },
    { label: 'Importe Neto', align: 'right', field: 'netAmount', value: (d) => Number(d.netAmount) || 0 },
    { label: 'Forma de envío', align: 'center', field: 'shippingMethod', value: (d) => textValue(d.shippingMethod), filterable: true },
    { label: 'Dirección Entrega', align: 'center', field: 'deliveryAddress', value: (d) => textValue(d.deliveryAddress) },
    { label: 'N° Documento', align: 'center', field: 'documentNumber', value: (d) => textValue(d.documentNumber), filterable: true },
    { label: 'N° Pedido', align: 'center', field: 'orderNumber', value: (d) => textValue(d.orderNumber), filterable: true },
    { label: 'N° Remito', align: 'center', field: 'remitoNumber', value: (d) => textValue(d.remitoNumber), filterable: true },
    { label: 'Bultos', align: 'center', field: 'packages', value: (d) => Number(d.packages) || 0 },
    { label: 'Nota', align: 'center' },
    { label: 'Seleccionar', align: 'center' },
]

const filterableColumns = columns.filter((c) => c.filterable)
// Desde cuántas filas nuevas a dibujar vale mostrar el spinner; con menos, el cambio es instantáneo
const MIN_FILAS_SPINNER = 80
// El Backdrop del spinner entra con un fade de 225 ms: el filtro se aplica cuando ya se ve, antes de congelar
const SPINNER_FADE_MS = 250
const headerContentSx = { display: 'inline-flex', alignItems: 'center' }
const sinFiltros = {}

// Valores repetidos de cada columna filtrable con su cantidad, de más a menos frecuente
const armarGrupos = (documentos) =>
    Object.fromEntries(filterableColumns.map((col) => {
        const counts = new Map()
        documentos.forEach((d) => {
            const value = col.value(d)
            counts.set(value, (counts.get(value) ?? 0) + 1)
        })
        const groups = [...counts].map(([value, count]) => ({ value, count }))
        groups.sort((a, b) => b.count - a.count || compareValues(a.value, b.value))
        return [col.field, groups]
    }))

// Un filtro elegido de la lista es exacto; uno escrito filtra por "contiene", sin distinguir mayúsculas
const cumpleFiltro = (doc, field, filtro) => {
    const value = columns.find((c) => c.field === field).value(doc)
    return filtro.exact ? value === filtro.value : value.toLowerCase().includes(filtro.value.toLowerCase())
}

const sinOrden = { field: null, direction: 'asc' }

// Cada clic en el encabezado pasa por ascendente → descendente → orden original
const nextOrden = (orden, field) => {
    if (orden.field !== field) return { field, direction: 'asc' }
    if (orden.direction === 'asc') return { field, direction: 'desc' }
    return sinOrden
}

const compareValues = (a, b) =>
    typeof a === 'number' && typeof b === 'number'
        ? a - b
        : a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' })

// Los tildados van primero para que queden juntos; dentro de cada grupo, la columna elegida o el orden de la API.
// Los filtros de los encabezados se combinan y no ocultan a los tildados, para que el recuento no pierda ninguno
const ordenarDocumentos = (documentos, selectedIds, orden, filtros) => {
    const column = columns.find((c) => c.field && c.field === orden.field)
    const sign = orden.direction === 'desc' ? -1 : 1
    const activos = Object.entries(filtros)
    return documentos
        .map((doc, index) => ({ doc, index, seleccionado: selectedIds.has(documentKey(doc)) }))
        .filter((item) => item.seleccionado || activos.every(([field, filtro]) => cumpleFiltro(item.doc, field, filtro)))
        .sort((a, b) => {
            if (a.seleccionado !== b.seleccionado) return a.seleccionado ? -1 : 1
            const byColumn = column ? sign * compareValues(column.value(a.doc), column.value(b.doc)) : 0
            return byColumn || a.index - b.index
        })
        .map((item) => item.doc)
}

export default function PlanillaRepartoTable({
    documentos,
    selectedIds,
    readOnly,
    onToggleSelect,
    onEditImporte,
    onBlurImporte,
    onEditDireccion,
    onEditBultos,
    onBlurBultos,
    onOpenNota,
    totales,
    canImprimir,
    onImprimir,
}) {
    const [orden, setOrden] = useState(sinOrden)
    // { [field]: { value, exact } } de los encabezados con filtro
    const [filtros, setFiltros] = useState(sinFiltros)

    // El orden se recalcula al cambiar los documentos, la selección, el orden o los filtros, no al editar una línea:
    // si no, la fila se movería mientras el operador escribe y perdería el foco
    const keysSignature = documentos.map(documentKey).join('\n')
    const selectedSignature = [...selectedIds].sort().join('\n')
    const ordenKeys = useMemo(
        () => ordenarDocumentos(documentos, selectedIds, orden, filtros).map(documentKey),
        [keysSignature, selectedSignature, orden, filtros] // eslint-disable-line react-hooks/exhaustive-deps
    )
    // Los grupos se arman una vez por cada lista nueva de documentos; las columnas filtrables no se editan
    const grupos = useMemo(
        () => armarGrupos(documentos),
        [keysSignature] // eslint-disable-line react-hooks/exhaustive-deps
    )
    const porKey = new Map(documentos.map((d) => [documentKey(d), d]))
    const ordenados = ordenKeys.map((key) => porKey.get(key)).filter(Boolean)
    const hayFiltros = Object.keys(filtros).length > 0

    // Sin documentos (Limpiar, otro transporte sin resultados) los filtros ya no tienen sobre qué aplicarse
    useEffect(() => {
        if (documentos.length === 0) setFiltros(sinFiltros)
    }, [documentos.length])

    // Quitar un filtro que dejaba pocas filas puede volver a dibujar cientos y congelar la pantalla unos segundos.
    // En ese caso se muestra el spinner global, se espera a que se pinte y recién ahí se aplica el filtro; el
    // spinner se oculta en el efecto, cuando la grilla ya está en pantalla
    const dispatch = useDispatch()
    const spinnerPendienteRef = useRef(false)
    const filtroTimeoutRef = useRef(null)
    const visiblesRef = useRef(0)
    visiblesRef.current = ordenKeys.length

    const aplicarFiltros = useCallback((next) => {
        // Con un filtro ya en espera, el último cambio reemplaza al anterior y usa el mismo spinner
        if (spinnerPendienteRef.current) {
            clearTimeout(filtroTimeoutRef.current)
            filtroTimeoutRef.current = setTimeout(() => setFiltros(next), SPINNER_FADE_MS)
            return
        }
        const filasNuevas = ordenarDocumentos(documentos, selectedIds, sinOrden, next).length - visiblesRef.current
        if (filasNuevas < MIN_FILAS_SPINNER) {
            setFiltros(next)
            return
        }
        spinnerPendienteRef.current = true
        dispatch(spinnerActions.show())
        filtroTimeoutRef.current = setTimeout(() => setFiltros(next), SPINNER_FADE_MS)
    }, [documentos, selectedIds, dispatch])

    useEffect(() => {
        if (!spinnerPendienteRef.current) return
        spinnerPendienteRef.current = false
        dispatch(spinnerActions.hide())
    }, [filtros, dispatch])

    // Si la pantalla se desmonta con el filtro en espera, el spinner no queda prendido
    useEffect(() => () => {
        clearTimeout(filtroTimeoutRef.current)
        if (spinnerPendienteRef.current) dispatch(spinnerActions.hide())
    }, [dispatch])

    const handleFiltroChange = useCallback((field, filtro) => {
        const { [field]: _, ...rest } = filtros
        aplicarFiltros(filtro ? { ...rest, [field]: filtro } : rest)
    }, [filtros, aplicarFiltros])

    return (
        <Box>
            <ActionsContainer sx={actionsSx}>
                <LeftActions>
                    <Typography variant="body2">
                        <strong>Neto Total:</strong> {formatMoney(totales.netoTotal)}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Total Bultos:</strong> {totales.totalBultos}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Documentos Seleccionados:</strong> {totales.documentosSeleccionados}
                    </Typography>
                    <Box sx={leyendaSx}>
                        <Box component="span" sx={notaLeyendaSx}>
                            <StickyNote2Icon sx={leyendaIconSx} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Nota iluminada: la línea tiene una nota
                        </Typography>
                    </Box>
                    {hayFiltros && (
                        <Box sx={leyendaSx}>
                            <Typography variant="body2" color="text.secondary">
                                Mostrando {ordenados.length} de {documentos.length} documentos
                            </Typography>
                            <Button size="small" onClick={() => aplicarFiltros(sinFiltros)}>
                                Quitar filtros
                            </Button>
                        </Box>
                    )}
                </LeftActions>
                <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={onImprimir}
                    disabled={!canImprimir}
                >
                    Imprimir
                </Button>
            </ActionsContainer>

            <TableContainer sx={tableContainerSx}>
                <StyledTable size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <StyledHeaderCell key={col.label} align={col.align}>
                                    {col.field ? (
                                        <Box sx={headerContentSx}>
                                            <TableSortLabel
                                                active={orden.field === col.field}
                                                direction={orden.field === col.field ? orden.direction : 'asc'}
                                                onClick={() => setOrden(nextOrden(orden, col.field))}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                            {col.filterable && (
                                                <ColumnFilter
                                                    label={col.label}
                                                    groups={grupos[col.field]}
                                                    filtro={filtros[col.field] ?? null}
                                                    onChange={(filtro) => handleFiltroChange(col.field, filtro)}
                                                />
                                            )}
                                        </Box>
                                    ) : (
                                        col.label
                                    )}
                                </StyledHeaderCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ordenados.length > 0 ? (
                            ordenados.map((doc) => {
                                const key = documentKey(doc)
                                return (
                                    <PlanillaRepartoRow
                                        key={key}
                                        doc={doc}
                                        seleccionado={selectedIds.has(key)}
                                        readOnly={readOnly}
                                        onToggleSelect={onToggleSelect}
                                        onEditImporte={onEditImporte}
                                        onBlurImporte={onBlurImporte}
                                        onEditDireccion={onEditDireccion}
                                        onEditBultos={onEditBultos}
                                        onBlurBultos={onBlurBultos}
                                        onOpenNota={onOpenNota}
                                    />
                                )
                            })
                        ) : (
                            <TableRow>
                                <StyledBodyCell colSpan={columns.length}>
                                    <EmptyState>
                                        {documentos.length > 0
                                            ? 'Ningún documento coincide con los filtros de los encabezados.'
                                            : 'Elegí un transporte y un rango de fechas para ver los documentos disponibles.'}
                                    </EmptyState>
                                </StyledBodyCell>
                            </TableRow>
                        )}
                    </TableBody>
                </StyledTable>
            </TableContainer>
        </Box>
    )
}
